import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  forceTransferOwnership,
  getOrganization,
  OrganizationDetail,
  OrganizationMemberItem,
} from "@/services/organizationsApi";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import {
  subscriptionBadgeColor,
  subscriptionBadgeLabel,
} from "@/lib/subscriptionBadge";
import { tierBadgeColor, tierBadgeLabel } from "@/lib/tierBadge";
import { roleBadgeColor, roleBadgeLabel } from "@/lib/roleBadge";
import OrganizationBillingGrantSection from "./OrganizationBillingGrantSection";
import OrganizationInvitationsSection from "./OrganizationInvitationsSection";
import OrganizationQuotasSection from "./OrganizationQuotasSection";

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Badge de rôle org — couleurs/libellés factorisés dans @/lib/roleBadge (DRY).
function roleBadge(role: string) {
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${roleBadgeColor(role)}`}>
      {roleBadgeLabel(role)}
    </span>
  );
}

function MetaRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 py-2 border-b last:border-b-0">
      <div className="text-xs uppercase tracking-wide text-gray-500 sm:w-56 shrink-0">
        {label}
      </div>
      <div className="text-sm break-all">{value ?? "-"}</div>
    </div>
  );
}

export default function OrganizationDetailPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const [org, setOrg] = useState<OrganizationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getOrganization(navigate, id);
        setOrg(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, id]);

  const handleForceTransfer = async (m: OrganizationMemberItem) => {
    if (!org) return;
    const reason = window.prompt(
      `Raison du transfert forcé de la propriété à ${m.user.email} (min. 5 caractères) :`
    );
    if (reason === null) return;
    if (reason.trim().length < 5) {
      window.alert("La raison doit faire au moins 5 caractères.");
      return;
    }
    const ok = await confirmDialog({
      title: "Désigner comme propriétaire",
      description: `Forcer ${m.user.email} comme unique OrgAdmin de « ${org.name} » ? Tous les autres OrgAdmin seront rétrogradés en OrgUser.`,
      confirmLabel: "Désigner propriétaire",
      destructive: true,
    });
    if (!ok) return;
    try {
      await forceTransferOwnership(navigate, org.id, m.user_id, reason.trim());
      const data = await getOrganization(navigate, id);
      setOrg(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur lors du transfert forcé");
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Link
              to="/organizations"
              className="text-sm text-blue-600 hover:underline"
            >
              ← Organisations
            </Link>
            {org && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                <h1 className="text-sm font-medium">{org.name}</h1>
              </>
            )}
          </div>
        </header>

        <div className="p-6 space-y-6">
          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          {org && (
            <>
              <OrganizationBillingGrantSection organizationId={org.id} />
              <OrganizationQuotasSection organizationId={org.id} />
              <section className="border rounded p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold">
                    {org.name}
                    <span className="ml-3 text-sm font-normal text-gray-500">
                      #{org.id}
                    </span>
                  </h2>
                  <div className="flex items-center gap-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        org.is_business
                          ? "bg-indigo-100 text-indigo-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {org.is_business ? "Entreprise" : "Perso (B2C)"}
                    </span>
                    {org.is_business && (
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${tierBadgeColor(org.tier)}`}
                      >
                        {tierBadgeLabel(org.tier)}
                      </span>
                    )}
                    <span
                      title="Abonnement B2C legacy"
                      className={`rounded px-2 py-0.5 text-xs font-medium ${subscriptionBadgeColor(
                        org.subscription_type
                      )}`}
                    >
                      {subscriptionBadgeLabel(org.subscription_type)}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <div>
                    <MetaRow label="Slug" value={<code className="font-mono">{org.slug}</code>} />
                    <MetaRow label="Membres" value={org.member_count} />
                    <MetaRow label="Équipes" value={org.team_count} />
                    <MetaRow label="Campagnes" value={org.campaign_count} />
                    <MetaRow
                      label="Sièges (actifs / achetés)"
                      value={`${org.active_seats} / ${org.purchased_seats}`}
                    />
                    <MetaRow label="Créée le" value={formatDate(org.created_at)} />
                    <MetaRow
                      label="Convertie entreprise"
                      value={formatDate(org.converted_business_at)}
                    />
                  </div>
                  <div>
                    <MetaRow label="Business name" value={org.business_name} />
                    <MetaRow
                      label="Business address"
                      value={org.business_address}
                    />
                    <MetaRow
                      label="Business email"
                      value={org.business_contact_email}
                    />
                    <MetaRow
                      label="SSO IdP alias"
                      value={
                        org.sso_idp_alias ? (
                          <code className="font-mono">{org.sso_idp_alias}</code>
                        ) : (
                          "-"
                        )
                      }
                    />
                    <MetaRow label="SSO statut" value={org.sso_idp_status} />
                    <MetaRow
                      label="Stripe customer"
                      value={
                        org.stripe_customer_id ? (
                          <code className="font-mono text-xs">
                            {org.stripe_customer_id}
                          </code>
                        ) : (
                          "-"
                        )
                      }
                    />
                    <MetaRow
                      label="Stripe subscription"
                      value={
                        org.stripe_subscription_id ? (
                          <code className="font-mono text-xs">
                            {org.stripe_subscription_id}
                          </code>
                        ) : (
                          "-"
                        )
                      }
                    />
                  </div>
                </div>
              </section>

              <section className="border rounded p-4 bg-white">
                <h3 className="text-md font-semibold mb-3">
                  Membres ({org.members.length})
                </h3>
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left">Nom</th>
                        <th className="px-3 py-2 text-left">Rôle</th>
                        <th className="px-3 py-2 text-left">Rejoint</th>
                        <th className="px-3 py-2 text-left">Validé</th>
                        <th className="px-3 py-2 text-left">Keycloak sub</th>
                        <th className="px-3 py-2 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {org.members.map((m) => (
                        <tr key={m.id} className="border-t">
                          <td className="px-3 py-2 font-mono text-xs">{m.user_id}</td>
                          <td className="px-3 py-2">{m.user.email}</td>
                          <td className="px-3 py-2">
                            {[m.user.first_name, m.user.last_name]
                              .filter(Boolean)
                              .join(" ") || "-"}
                          </td>
                          <td className="px-3 py-2">{roleBadge(m.role)}</td>
                          <td className="px-3 py-2">{formatDate(m.joined_at)}</td>
                          <td className="px-3 py-2">{formatDate(m.validated_at)}</td>
                          <td className="px-3 py-2">
                            <Link
                              to={`/users/${encodeURIComponent(m.user.keycloak_sub)}`}
                              className="font-mono text-xs text-blue-600 hover:underline"
                              title="Ouvrir la fiche utilisateur"
                            >
                              {m.user.keycloak_sub.substring(0, 12)}...
                            </Link>
                          </td>
                          <td className="px-3 py-2">
                            {m.role !== "PendingUser" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleForceTransfer(m)}
                                title="Désigner ce membre comme propriétaire (OrgAdmin unique)"
                              >
                                Désigner propriétaire
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {!org.members.length && (
                        <tr>
                          <td
                            className="px-3 py-3 text-center text-gray-500"
                            colSpan={8}
                          >
                            Aucun membre
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <OrganizationInvitationsSection organizationId={org.id} />

              <section className="border rounded p-4 bg-white">
                <h3 className="text-md font-semibold mb-3">
                  Équipes ({org.teams.length})
                </h3>
                <div className="space-y-4">
                  {(() => {
                    // Rôle org par user_id (les TeamMember ne portent pas le rôle).
                    const roleByUserId = new Map(
                      org.members.map((m) => [m.user_id, m.role] as const)
                    );
                    return org.teams.map((t) => (
                    <div
                      key={t.id}
                      className="border rounded p-3 bg-gray-50/50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">
                          {t.name}{" "}
                          <span className="ml-2 text-xs text-gray-500">
                            #{t.id} · créée {formatDate(t.created_at)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-600">
                          {t.member_count} membre{t.member_count > 1 ? "s" : ""}
                        </span>
                      </div>
                      {t.members.length ? (
                        <ul className="text-xs space-y-1">
                          {t.members.map((tm) => (
                            <li key={tm.user_id} className="font-mono">
                              {tm.email}
                              {(tm.first_name || tm.last_name) && (
                                <span className="ml-2 text-gray-500">
                                  ({[tm.first_name, tm.last_name]
                                    .filter(Boolean)
                                    .join(" ")})
                                </span>
                              )}
                              {roleByUserId.get(tm.user_id) && (
                                <span className="ml-2 font-sans">
                                  {roleBadge(roleByUserId.get(tm.user_id)!)}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-500 italic">
                          Aucun membre dans cette équipe.
                        </p>
                      )}
                    </div>
                    ));
                  })()}
                  {!org.teams.length && (
                    <p className="text-sm text-gray-500 italic">
                      Aucune équipe créée dans cette organisation.
                    </p>
                  )}
                </div>
              </section>

              <div>
                <Button
                  variant="outline"
                  onClick={() => navigate("/organizations")}
                >
                  Retour à la liste
                </Button>
              </div>
            </>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
